import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  ApiError,
  acceptInvitation,
  getInvitationPreview,
  type InvitationPreview,
} from "@/services/api";

const MIN_PASSWORD_LENGTH = 7;

// Map the specific error codes we expect during preview + accept to messages
// the invitee can act on. Falls back to the raw server message.
const prettyError = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError && err.code) {
    const map: Record<string, string> = {
      INVITATION_NOT_FOUND: "This invitation link is invalid. Ask your admin for a new one.",
      INVITATION_EXPIRED: "This invitation has expired. Ask your admin to send a new one.",
      INVITATION_REVOKED: "This invitation has been revoked. Ask your admin for a new one.",
      INVITATION_ALREADY_ACCEPTED: "This invitation has already been accepted. Try signing in instead.",
      INVITATION_NOT_PENDING: "This invitation is no longer active. Ask your admin for a new one.",
      EMAIL_ALREADY_REGISTERED: "An account already exists for this email. Try signing in instead.",
      FIREBASE_USER_EXISTS: "An account already exists for this email. Try signing in instead.",
      PASSWORD_TOO_WEAK: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      INVALID_FULL_NAME: "Please enter your full name.",
    };
    if (map[err.code]) return map[err.code];
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

// Client-side mirror of the backend policy so users get fast feedback.
// Server-side validation is still authoritative.
const validatePassword = (pw: string): string | null => {
  if (pw.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  return null;
};

const AcceptInvite = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [previewLoading, setPreviewLoading] = useState(true);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setPreviewError("Missing invitation token.");
      setPreviewLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await getInvitationPreview(token);
        setPreview(data);
      } catch (err) {
        setPreviewError(prettyError(err, "We couldn't load this invitation."));
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmed = fullName.trim();
    if (!trimmed) {
      setFormError("Please enter your full name.");
      return;
    }
    const pwError = validatePassword(password);
    if (pwError) {
      setFormError(pwError);
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    if (!token) return;

    setSubmitting(true);
    try {
      const result = await acceptInvitation(token, trimmed, password);
      // Sign in with the custom token the server returned. This drops us into
      // an authenticated session; AuthContext's onAuthStateChanged listener
      // will then fetch /auth/me and populate the app user. No email
      // verification round-trip: the server created the Firebase user with
      // emailVerified=true.
      await signInWithCustomToken(auth, result.customToken);
      setDone(true);
      toast({
        title: "Welcome to the team!",
        description: `You've joined ${preview?.companyName ?? "the workspace"}.`,
      });
      // Small delay so the success state is visible before we switch pages.
      setTimeout(() => navigate("/app"), 1200);
    } catch (err) {
      setFormError(prettyError(err, "We couldn't accept this invitation."));
    } finally {
      setSubmitting(false);
    }
  };

  // Invite preview failed (bad/expired/revoked/already-accepted token) —
  // render a dedicated terminal state instead of the form.
  if (!previewLoading && previewError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle>Invitation unavailable</CardTitle>
            <CardDescription>{previewError}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Link to="/login">
              <Button variant="outline" className="w-full">Go to sign in</Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="w-full">Back to home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (previewLoading || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your invitation…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src="/logo.png" alt="senloop" className="w-10 h-10 rounded-lg" />
            <span className="font-brand text-4xl font-extrabold tracking-tight text-foreground">senloop</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">You're invited!</h1>
          <p className="text-muted-foreground mt-2">
            {preview.invitedByName ? (
              <>
                <span className="font-medium">{preview.invitedByName}</span> invited you to join{" "}
                <span className="font-medium">{preview.companyName}</span>
              </>
            ) : (
              <>
                You've been invited to join <span className="font-medium">{preview.companyName}</span>
              </>
            )}
            {" "}as a <span className="font-medium">{preview.role.toLowerCase()}</span>.
          </p>
        </div>

        <Card className="shadow-lg">
          {done ? (
            <CardContent className="py-10 text-center space-y-4">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">You're in!</h3>
              <p className="text-sm text-muted-foreground">
                Signing you in and taking you to the dashboard…
              </p>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Set up your account</CardTitle>
                <CardDescription>
                  You'll sign in with <span className="font-medium">{preview.email}</span>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit} className="space-y-4">
                  {formError && (
                    <Alert variant="destructive">
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      required
                      autoFocus
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your full name"
                      disabled={submitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password"
                      disabled={submitting}
                    />
                    <p className="text-xs text-gray-500">
                      At least {MIN_PASSWORD_LENGTH} characters.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      disabled={submitting}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Creating your account…" : "Accept & create account"}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Invitation expires {new Date(preview.expiresAt).toLocaleDateString()}.
                  </p>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvite;
